/**
 * Реалтайм-канал: по сокету приходит только новый `seq`, за данными движок идёт
 * в `pull` (раздел 5.2 архитектуры). Поэтому обрыв ничего не теряет — потерянный
 * звоночек заменяется опросом, а переподключение чинит себя само.
 */

export interface StreamOptions {
  /** Адрес сокета. Пусто — тот же источник, что и страница. */
  baseUrl?: string;
  accessToken: () => Promise<string>;
  onBump: (seq: number) => void;
  onOpen?: () => void;
  onClose?: (code: number) => void;
  random?: () => number;
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
  socketFactory?: (url: string) => WebSocket;
}

export interface StreamHandle {
  start(): void;
  stop(): void;
  connected(): boolean;
}

export function createStream(options: StreamOptions): StreamHandle {
  const random = options.random ?? (() => Math.random());
  const baseMs = options.reconnectBaseMs ?? 1_000;
  const maxMs = options.reconnectMaxMs ?? 30_000;

  let socket: WebSocket | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;
  let stopped = true;

  function url(): string | undefined {
    if (options.baseUrl !== undefined && options.baseUrl.length > 0) {
      return `${options.baseUrl.replace(/\/$/, '')}/v1/sync/stream`;
    }

    // Вне браузера адреса страницы нет, и вычислить адрес сокета не из чего.
    const location = globalThis.location;
    if (location === undefined) return undefined;

    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${scheme}//${location.host}/v1/sync/stream`;
  }

  function open(): void {
    if (stopped) return;

    const target = url();
    // Канал необязателен: без него правки забирает опрос, только с задержкой.
    // Ронять из-за него запуск нельзя — приложение работает и без реалтайма.
    if (target === undefined) return;

    const ws = (options.socketFactory ?? ((address: string) => new WebSocket(address)))(target);
    socket = ws;

    ws.addEventListener('open', () => {
      // Токен уходит первым сообщением, а не в адресе: адреса оседают в логах
      // прокси и в истории браузера.
      void options.accessToken().then(
        (token) => {
          ws.send(JSON.stringify({ type: 'auth', token }));
        },
        () => {
          // Токен не выдали — сессии нет. Вот теперь переподключаться
          // действительно незачем, и канал закрывается насовсем: поднимет его
          // `start` после нового входа.
          stopped = true;
          ws.close();
        },
      );
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      const message = parse(event.data);
      if (message === undefined) return;

      if (message.type === 'ready') {
        attempt = 0;
        options.onOpen?.();
        return;
      }
      if (message.type === 'ping') {
        ws.send(JSON.stringify({ type: 'pong' }));
        return;
      }
      if (message.type === 'bump' && typeof message.seq === 'number') {
        options.onBump(message.seq);
      }
    });

    ws.addEventListener('close', (event: CloseEvent) => {
      socket = undefined;
      options.onClose?.(event.code);

      // 4401 значит «токен не годится» — но с тем же токеном мы и не
      // переподключаемся: при следующей попытке `accessToken` сам сходит за
      // свежим. Раньше здесь стоял безусловный выход, и это была ловушка:
      // канал замолкал навсегда, а `stopped` при этом оставался снятым, так
      // что и `start` после нового входа ничего не поднимал — реалтайм не
      // возвращался до перезагрузки вкладки. Если сессии действительно нет,
      // попытку остановит отказ `accessToken` в обработчике `open`.
      reconnect();
    });
  }

  function reconnect(): void {
    if (stopped || timer !== undefined) return;

    const delay = Math.min(baseMs * 2 ** attempt, maxMs);
    attempt += 1;

    timer = setTimeout(
      () => {
        timer = undefined;
        open();
      },
      Math.round(delay * (0.5 + random() * 0.5)),
    );
  }

  return {
    start(): void {
      if (!stopped) return;
      stopped = false;
      attempt = 0;
      open();
    },

    stop(): void {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      socket?.close();
      socket = undefined;
    },

    connected: () => socket?.readyState === 1,
  };
}

function parse(raw: unknown): { type: string; seq?: unknown } | undefined {
  if (typeof raw !== 'string') return undefined;

  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value === 'object' && value !== null && 'type' in value) {
      return value as { type: string; seq?: unknown };
    }
  } catch {
    // Мусор в канале — не повод падать: следующий звоночек всё равно придёт.
  }
  return undefined;
}
